import {
  ERROR_LEVELS,
  ERROR_SOURCES,
  type ErrorGroup,
  type ErrorLogSummary,
  type PageInfo,
} from '@atlas-control/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';
import { PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { StatusBadge, type Tone } from '@/components/status-badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { formatDateTime, formatRelative } from '@/lib/format';
import { guard } from '@/lib/guards';
import { pageSearch } from '@/lib/page-search';

const Search = z.object({
  view: z.enum(['list', 'groups']).default('groups'),
  source: z.enum(ERROR_SOURCES).optional(),
  level: z.enum(ERROR_LEVELS).optional(),
  resolved: z.enum(['yes', 'no']).optional().default('no'),
  requestId: z.string().optional(),
  runId: z.string().optional(),
  page: pageSearch,
});
type SearchT = z.infer<typeof Search>;

export const Route = createFileRoute('/_app/errors/')({
  beforeLoad: ({ context }) => guard(context.me, 'errors.read'),
  validateSearch: Search,
  component: ErrorsPage,
});

const ALL = '__all__';
const LEVEL_TONE: Record<string, Tone> = { warn: 'warning', error: 'danger', fatal: 'danger' };

function ErrorsPage() {
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<SearchT>) =>
    void navigate({ search: (p) => ({ ...p, ...patch, page: undefined }) });
  const setPage = useCallback(
    (n: number) => void navigate({ search: (p) => ({ ...p, page: n > 1 ? n : undefined }) }),
    [navigate],
  );
  const page = s.page ?? 1;
  const query = {
    source: s.source,
    level: s.level,
    resolved: s.resolved,
    requestId: s.requestId,
    runId: s.runId,
  };

  return (
    <>
      <PageHeader title="Errors" />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={s.view} onValueChange={(v) => set({ view: v as SearchT['view'] })}>
          <TabsList>
            <TabsTrigger value="groups">Grouped</TabsTrigger>
            <TabsTrigger value="list">All occurrences</TabsTrigger>
          </TabsList>
        </Tabs>
        <Pick
          label="Source"
          value={s.source}
          options={ERROR_SOURCES}
          onChange={(v) => set({ source: v as SearchT['source'] })}
        />
        <Pick
          label="Level"
          value={s.level}
          options={ERROR_LEVELS}
          onChange={(v) => set({ level: v as SearchT['level'] })}
        />
        <Pick
          label="State"
          value={s.resolved}
          options={['no', 'yes']}
          labels={{ no: 'Unresolved', yes: 'Resolved' }}
          onChange={(v) => set({ resolved: v as SearchT['resolved'] })}
        />
        <Input
          className="w-64"
          placeholder="Request id"
          defaultValue={s.requestId}
          onKeyDown={(e) =>
            e.key === 'Enter' &&
            set({
              requestId: (e.target as HTMLInputElement).value.trim() || undefined,
              view: 'list',
            })
          }
          aria-label="Find by request id"
        />
      </div>
      {s.view === 'groups' ? (
        <Groups query={query} page={page} setPage={setPage} />
      ) : (
        <List query={query} page={page} setPage={setPage} />
      )}
    </>
  );
}

function Pick({
  label,
  value,
  options,
  labels,
  onChange,
}: {
  label: string;
  value: string | undefined;
  options: readonly string[];
  labels?: Record<string, string>;
  onChange: (v: string | undefined) => void;
}) {
  return (
    <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
      <SelectTrigger className="w-40" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{label}: all</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {label}: {labels?.[o] ?? o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface ListProps {
  query: Record<string, string | undefined>;
  page: number;
  setPage: (page: number) => void;
}

function Groups({ query, page, setPage }: ListProps) {
  const q = useQuery({
    queryKey: ['errors', 'groups', query, page],
    queryFn: () =>
      api<{ groups: ErrorGroup[] } & PageInfo>('/errors/groups', { query: { ...query, page } }),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <Card className="py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Latest message</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">Count</TableHead>
              <TableHead>First seen</TableHead>
              <TableHead>Last seen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data?.groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  No errors match.
                </TableCell>
              </TableRow>
            )}
            {q.data?.groups.map((g) => (
              <TableRow key={g.fingerprint}>
                <TableCell className="font-mono text-xs">
                  <Link to="/errors/$errorId" params={{ errorId: g.latestId }}>
                    {g.code}
                  </Link>
                </TableCell>
                <TableCell className="max-w-md truncate">{g.message}</TableCell>
                <TableCell>{g.source}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {g.count}
                  {g.unresolved < g.count && (
                    <span className="text-muted-foreground"> ({g.unresolved} open)</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatRelative(g.firstSeen)}
                </TableCell>
                <TableCell title={formatDateTime(g.lastSeen)}>
                  {formatRelative(g.lastSeen)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination info={q.data} onPageChange={setPage} />
    </>
  );
}

function List({ query, page, setPage }: ListProps) {
  const q = useQuery({
    queryKey: ['errors', 'list', query, page],
    queryFn: () =>
      api<{ errors: ErrorLogSummary[] } & PageInfo>('/errors', { query: { ...query, page } }),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.errors ?? [];
  return (
    <>
      <Card className="py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Where</TableHead>
              <TableHead>User</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatDateTime(e.occurredAt)}
                </TableCell>
                <TableCell>
                  <StatusBadge tone={LEVEL_TONE[e.level] ?? 'neutral'}>{e.level}</StatusBadge>
                </TableCell>
                <TableCell>{e.source}</TableCell>
                <TableCell className="font-mono text-xs">
                  <Link to="/errors/$errorId" params={{ errorId: e.id }}>
                    {e.code}
                  </Link>
                </TableCell>
                <TableCell className="max-w-sm truncate">{e.message}</TableCell>
                <TableCell className="font-mono text-xs">{e.jobName ?? e.path ?? '-'}</TableCell>
                <TableCell>{e.user?.name ?? '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination info={q.data} onPageChange={setPage} />
    </>
  );
}
